/**
 * Authentication utilities
 */

import { storage } from './storage';

const USER_TOKEN_KEY = 'userToken';
const USER_ROLE_KEY = 'userRole';
const GOOGLE_CREDENTIAL_KEY = 'googleCredential';

export type UserRole = 'admin' | 'user';

export const auth = {
  /**
   * Get the current user token
   */
  getToken(): string | null {
    return storage.getString(USER_TOKEN_KEY);
  },

  /**
   * Set the user token
   */
  setToken(token: string): void {
    storage.setString(USER_TOKEN_KEY, token);
  },

  /**
   * Remove the user token (logout)
   */
  removeToken(): void {
    storage.remove(USER_TOKEN_KEY);
  },

  /**
   * Get the current user role
   */
  getRole(): UserRole | null {
    return storage.getString(USER_ROLE_KEY) as UserRole | null;
  },

  /**
   * Set the user role
   */
  setRole(role: UserRole): void {
    storage.setString(USER_ROLE_KEY, role);
  },

  /**
   * Remove the user role
   */
  removeRole(): void {
    storage.remove(USER_ROLE_KEY);
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getToken() !== null;
  },

  /**
   * Check if user is an admin
   */
  isAdmin(): boolean {
    return this.getRole() === 'admin';
  },

  /**
   * Get user email (token is the email)
   */
  getUserEmail(): string | null {
    return this.getToken();
  },

  /**
   * Get Google OAuth credential (ID token)
   */
  getGoogleCredential(): string | null {
    return storage.getString(GOOGLE_CREDENTIAL_KEY);
  },

  /**
   * Set Google OAuth credential
   */
  setGoogleCredential(credential: string): void {
    storage.setString(GOOGLE_CREDENTIAL_KEY, credential);
  },

  /**
   * Remove Google OAuth credential
   */
  removeGoogleCredential(): void {
    storage.remove(GOOGLE_CREDENTIAL_KEY);
  },

  /**
   * Clear all auth data
   */
  clearAuth(): void {
    this.removeToken();
    this.removeRole();
    this.removeGoogleCredential();
  },
};
